import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { durationLabel, isoDay, longDay, parseDay } from '../../lib/quickAdd';
import {
  DEFAULT_BLOCK, SNAP, blockOf, daysFor, eventDays, feedItems, feedWhen, gridHours, layoutDay, localOf, rangeTitle,
  snap, step, toClock, toMinutes
} from '../../lib/todoCalendar';
import { FeedPopover, LayersPicker } from './CalendarLayers';
import CalendarSubscribe from './CalendarSubscribe';
import { deadlineState } from '../todoViews';
import { swatch } from './MoreMenu';
import { Icon } from './icons';
import './TodoCalendar.css';

/*
 * THE CALENDAR (073).
 *
 * Gordon: time-blocking - the to-dos laid out on the week, the way Todoist's
 * and Google Calendar's are, with the meetings and appointments that already
 * fill it, so a two-hour job can be put where two hours are free.
 *
 * WHAT IS ON IT. Every open to-do with a due date in the range: one with a
 * TIME is a block from that time, as long as its duration (half an hour when
 * it has none); one without a time sits in the ALL-DAY row of its day. A
 * DEADLINE is a flag on its own day. The viewer's MEETINGS (066) and the
 * ticket APPOINTMENTS they are part of (027) are drawn too, read-only and in
 * a different style - GET /todos/calendar has whose are whose.
 *
 * A REPEATING TO-DO is shown on its next date only - the date it has. The
 * repeat engine (058) works one occurrence at a time, and a column of
 * "Monday payroll" blocks that cannot be moved one by one would promise
 * something the list cannot do. Its block carries the ↻, and the foot of
 * the calendar says so.
 *
 * THE VIEWS. On a desk: Week (the default - a grid from 07:00 to 20:00,
 * wider when something is outside it, scrolled to now) and Month; Day as
 * well. On a phone: 3 days (the default), Day, and Agenda - the next
 * fortnight as a list. Today, ‹ and ›; on the keyboard ← and → move, T
 * comes back to today.
 *
 * CHANGING THINGS. Drag a block to another time or day (15-minute steps), or
 * into the all-day row to take its time off; drag an all-day to-do into the
 * grid to give it one; drag the bottom edge of a block to change how long it
 * takes. In Month, drag a to-do to another day. Click an empty slot to add a
 * to-do there (the editor opens with that day and time); click a block to
 * open it. Keyboard: a focused block moves with Shift+arrows (a day, or 15
 * minutes), and Enter opens it. Nothing is changed here directly - the page
 * sends it (onMove, onResize) the way every other change is sent.
 *
 * FILTERS: list, label and client (072), the page's own pieces of a to-do.
 *
 * EVENT CALENDARS (077): the public calendars the firm offers - the MCC,
 * Events in Gozo, Malta's public holidays - as LAYERS the person switches on
 * in "Calendars" (CalendarLayers.js). GET /todos/calendar sends the layers
 * and the events of the ones that are on. They are somebody else's events:
 * read-only, plain with a thin stripe of the calendar's colour and a
 * calendar mark - never the raised card of a to-do or the tint of a meeting
 * - all-day ones (and anything a day long or more) in the all-day row, small
 * chips in Month; a click opens their card (Open event page, Add to my
 * to-dos). Before 077 the answer has no layers and none of this shows.
 */

const HOUR_PX = 48;
const PX = HOUR_PX / 60;          // pixels per minute
const DRAG_AFTER = 4;             // px before a press becomes a drag
const SHOW_IN_MONTH = 3;          // entries in a month cell before "+N more"
const AGENDA_DAYS = 14;

// What each width offers, the first being where it starts. A tablet, or a
// desk with the sidebar taking its share, starts on three days: seven
// columns of 60 pixels make blocks nobody can read.
const VIEWS_BY_SIZE = {
  desk: [{ key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }, { key: 'day', label: 'Day' }],
  medium: [{ key: 'days3', label: '3 days' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' },
    { key: 'day', label: 'Day' }],
  phone: [{ key: 'days3', label: '3 days' }, { key: 'day', label: 'Day' }, { key: 'agenda', label: 'Agenda' }]
};

/** Whether a media query matches, kept up to date. */
const useMatch = (q) => {
  const get = () => Boolean(window.matchMedia && window.matchMedia(q).matches);
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    f();
    if (m.addEventListener) m.addEventListener('change', f); else m.addListener(f);
    return () => { if (m.removeEventListener) m.removeEventListener('change', f); else m.removeListener(f); };
  }, [q]);
  return on;
};

const read = (k, fallback) => { try { return localStorage.getItem(k) || fallback; } catch { return fallback; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* not kept */ } };

const dayOf = (t) => (t.due_date ? String(t.due_date).slice(0, 10) : null);
const deadlineOf = (t) => (t.deadline ? String(t.deadline).slice(0, 10) : null);
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const clockOf = (iso) => { const l = localOf(iso); return l ? toClock(l.min) : ''; };
const span = (a, b) => `${a}–${b}`;

/** A meeting or appointment as the calendar draws it. */
const eventsFrom = (feed) => [
  ...(feed.meetings || []).map((m) => ({
    key: `m:${m.id}`, kind: 'meeting', title: m.title, starts_at: m.starts_at, ends_at: m.ends_at,
    where: m.kind === 'room' ? (m.location || 'In the room') : 'Online', recurring: m.recurring,
    response: m.my_response, organiser: m.is_organiser, href: `/meetings/${m.id}`
  })),
  ...(feed.appointments || []).map((a) => ({
    key: `a:${a.id}`, kind: 'appointment', title: `${a.ticket_number} · ${a.ticket_title || 'Appointment'}`,
    starts_at: a.starts_at, ends_at: a.ends_at,
    where: a.kind === 'physical' ? (a.location || 'On site') : 'Online', href: `/tickets/${a.ticket_id}`
  }))
];

export default function TodoCalendar({
  items, lists = [], labels = [], companies = null, todayIso, now, loadFeed, canDuration = false,
  narrow = false, busy = false, onMove, onResize, onCreate, onOpen, onGo, saveLayers = null, onAddEvent = null,
  store = '', defaultView = null, preset = null, subscribe = null
}) {
  /*
   * Oct 2026: the calendar is a LAYOUT of a page (TodoApp's List / Board /
   * Calendar switch) rather than a page of its own. Each page keeps its own
   * choice of Day / Week / Month (`store`), Today starts on Day
   * (`defaultView`), and a list or label opens with its filter already set
   * (`preset`: { list, label }).
   */
  const medium = useMatch('(max-width: 1100px)');
  const size = narrow ? 'phone' : medium ? 'medium' : 'desk';
  const viewKey = `tda-cal-view:${size}${store}`;
  const allowed = VIEWS_BY_SIZE[size];
  const first = defaultView && allowed.some((x) => x.key === defaultView) ? defaultView : allowed[0].key;
  const [view, setViewState] = useState(() => {
    const v = read(viewKey, first);
    return allowed.some((x) => x.key === v) ? v : first;
  });
  useEffect(() => {
    const v = read(viewKey, first);
    setViewState(allowed.some((x) => x.key === v) ? v : first);
    // The set of views changes only with the size (and the page).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, viewKey]);
  const [showFilters, setShowFilters] = useState(false);
  const setView = (v) => { setViewState(v); write(viewKey, v); };
  const [anchor, setAnchor] = useState(todayIso);
  const [filter, setFilter] = useState(() => ({ list: '', label: '', client: '',
                                                ...(preset ? { list: preset.list || '', label: preset.label || '' } : {}) }));
  const presetList = preset ? preset.list || '' : null;
  const presetLabel = preset ? preset.label || '' : null;
  useEffect(() => {
    if (presetList === null) return;
    setFilter((f) => ({ ...f, list: presetList, label: presetLabel || '' }));
  }, [presetList, presetLabel]);
  const [feed, setFeed] = useState({ meetings: [], appointments: [] });
  const [feedNote, setFeedNote] = useState(null);
  // 077: the event calendars, and the one whose card is open.
  const [layers, setLayers] = useState([]);
  const [layersSaving, setLayersSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [pop, setPop] = useState(null);
  const popFrom = useRef(null);
  const [drag, setDrag] = useState(null);   // the preview while something is dragged
  const root = useRef(null);
  const scroller = useRef(null);
  const press = useRef(null);

  const days = useMemo(() => (view === 'agenda'
    ? Array.from({ length: AGENDA_DAYS }, (_, i) => isoDay(new Date(parseDay(anchor).getFullYear(),
      parseDay(anchor).getMonth(), parseDay(anchor).getDate() + i)))
    : daysFor(view, anchor)), [view, anchor]);
  const from = days[0];
  const to = days[days.length - 1];

  // Meetings and appointments for what is shown. A server without the feed
  // (404) or a failure leaves the calendar to the to-dos, and says so once.
  useEffect(() => {
    if (!loadFeed) return undefined;
    let live = true;
    loadFeed(from, to)
      .then((r) => {
        if (!live) return;
        const v = r && typeof r === 'object' ? r : {};
        setFeed(v);
        if (Array.isArray(v.layers)) setLayers(v.layers);
        setFeedNote(null);
      })
      .catch(() => { if (live) { setFeed({}); setFeedNote('Meetings and appointments could not be loaded just now.'); } });
    return () => { live = false; };
  }, [loadFeed, from, to, reload]);

  /** A layer switched on or off: shown at once, saved, and the events read again. */
  const toggleLayer = async (layer, on) => {
    if (!saveLayers) return;
    const before = layers;
    setLayers((ls) => ls.map((l) => (l.id === layer.id ? { ...l, on } : l)));
    setLayersSaving(true);
    try {
      const r = await saveLayers({ [layer.id]: on });
      if (r && Array.isArray(r.layers)) setLayers(r.layers);
      setFeedNote(null);
      if (on) setReload((n) => n + 1);
    } catch {
      setLayers(before);
      setFeedNote(`“${layer.name}” could not be ${on ? 'shown' : 'hidden'} just now - try again.`);
    } finally {
      setLayersSaving(false);
    }
  };

  const feeds = useMemo(() => feedItems(feed.feed_events, layers), [feed, layers]);
  const openFeed = (item, el) => {
    popFrom.current = el || null;
    setPop({ item, at: el ? el.getBoundingClientRect() : null });
  };
  const closePop = useCallback(() => {
    setPop(null);
    const el = popFrom.current;
    popFrom.current = null;
    if (el && document.contains(el)) el.focus();
  }, []);

  /* ------------------------------------------------------------ the to-dos */

  const shown = useMemo(() => items.filter((t) => (!filter.list || t.list_id === filter.list)
    && (!filter.label || (t.labels || []).includes(filter.label))
    && (!filter.client || t.company_id === filter.client)), [items, filter]);
  const inRange = (d) => d && d >= from && d <= to;
  const byDay = useMemo(() => {
    const m = new Map(days.map((d) => [d, { timed: [], untimed: [], deadlines: [], events: [], feeds: [] }]));
    for (const t of shown) {
      const d = dayOf(t);
      if (inRange(d)) (blockOf(t) ? m.get(d).timed : m.get(d).untimed).push(t);
      const dl = deadlineOf(t);
      if (inRange(dl) && m.has(dl)) m.get(dl).deadlines.push(t);
    }
    for (const e of eventsFrom(feed)) {
      for (const part of eventDays(e.starts_at, e.ends_at)) {
        if (m.has(part.day)) m.get(part.day).events.push({ ...e, start: part.start, end: part.end });
      }
    }
    // 077: the event calendars - all-day in the all-day row, the rest beside the meetings.
    for (const f of feeds) {
      if (f.allDay) {
        for (const d of f.days) if (m.has(d)) m.get(d).feeds.push(f);
        continue;
      }
      for (const part of eventDays(f.starts_at, f.ends_at)) {
        if (m.has(part.day)) m.get(part.day).events.push({ ...f, kind: 'feed', start: part.start, end: part.end });
      }
    }
    for (const v of m.values()) {
      v.untimed.sort((a, b) => (Number(a.priority) || 4) - (Number(b.priority) || 4) || String(a.title).localeCompare(String(b.title)));
    }
    return m;
    // inRange reads from/to, which days already carries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, feed, feeds, days]);

  const isGrid = view === 'week' || view === 'day' || view === 'days3';
  const hours = useMemo(() => {
    const spans = [];
    for (const v of byDay.values()) {
      for (const t of v.timed) spans.push(blockOf(t));
      for (const e of v.events) spans.push(e);
    }
    return gridHours(spans);
  }, [byDay]);

  // Scrolled so the morning (or now, when it is today) is in sight.
  const scrolledFor = useRef(null);
  useLayoutEffect(() => {
    if (!isGrid || !scroller.current) return;
    const key = `${view}:${from}`;
    if (scrolledFor.current === key) return;
    scrolledFor.current = key;
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const target = days.includes(todayIso) ? Math.max(hours.from, nowMin - 90) : Math.max(hours.from, 8 * 60);
    scroller.current.scrollTop = Math.max(0, (target - hours.from) * PX);
  }, [isGrid, view, from, days, todayIso, now, hours.from]);

  /* ----------------------------------------------------------- navigation */

  const go = useCallback((dir) => setAnchor((a) => step(view, a, dir)), [view]);
  const goToday = useCallback(() => setAnchor(todayIso), [todayIso]);

  const kbRef = useRef(null);
  kbRef.current = { go, goToday };
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (document.querySelector('.tds-wrap, .ctd-sheet, .tcal-pop, .tcal-layers-pop')) return;
      if (el && el.closest && el.closest('[data-cal-item]') && e.shiftKey) return;   // the block's own
      if (e.key === 'ArrowLeft') { e.preventDefault(); kbRef.current.go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); kbRef.current.go(1); }
      else if (e.key === 't' || e.key === 'T') { e.preventDefault(); kbRef.current.goToday(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* --------------------------------------------------------------- moving */

  /** Where the pointer is: a grid column (with a minute), an all-day cell, or a month cell. */
  const targetAt = (x, y, grabMin, len) => {
    const box = root.current;
    if (!box) return null;
    for (const el of box.querySelectorAll('[data-cal-allday]')) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) return { day: el.dataset.calAllday, time: null };
    }
    for (const el of box.querySelectorAll('[data-cal-mday]')) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) return { day: el.dataset.calMday, keep: true };
    }
    for (const el of box.querySelectorAll('[data-cal-day]')) {
      const r = el.getBoundingClientRect();
      if (x < r.left || x >= r.right) continue;
      const sc = scroller.current ? scroller.current.getBoundingClientRect() : r;
      if (y < sc.top - 4 || y > sc.bottom + 4) return null;
      const raw = hours.from + (y - r.top) / PX - grabMin;
      const start = Math.max(hours.from, Math.min(snap(raw), hours.to - Math.min(len, hours.to - hours.from)));
      return { day: el.dataset.calDay, time: toClock(Math.max(0, Math.min(start, 24 * 60 - SNAP))) };
    }
    return null;
  };

  const startMove = (e, item, { grabMin = 0 } = {}) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (busy) return;
    e.stopPropagation();
    const b = blockOf(item);
    press.current = { kind: 'move', item, x: e.clientX, y: e.clientY, grabMin, len: b ? b.end - b.start : DEFAULT_BLOCK,
                      moved: false };
    const move = (ev) => {
      const p = press.current;
      if (!p) return;
      if (!p.moved && Math.hypot(ev.clientX - p.x, ev.clientY - p.y) < DRAG_AFTER) return;
      p.moved = true;
      ev.preventDefault();
      const t = targetAt(ev.clientX, ev.clientY, p.grabMin, p.len);
      p.target = t;
      setDrag({ id: item.id, kind: 'move', target: t, len: p.len, title: item.title, x: ev.clientX, y: ev.clientY });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      const p = press.current;
      press.current = null;
      setDrag(null);
      if (!p) return;
      if (!p.moved) { onOpen(item); return; }
      const t = p.target;
      if (!t) return;
      const next = { due_date: t.day, due_time: t.keep ? (item.due_time || null) : t.time };
      if (next.due_date === dayOf(item) && (next.due_time || null) === (item.due_time || null)) return;
      onMove(item, next);
    };
    const cancel = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      press.current = null;
      setDrag(null);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  };

  const startResize = (e, item) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (busy || !canDuration) return;
    e.stopPropagation();
    e.preventDefault();
    const b = blockOf(item);
    if (!b) return;
    const y0 = e.clientY;
    const len0 = b.end - b.start;
    const max = Math.min(24 * 60 - b.start, 24 * 60);
    let len = len0;
    const move = (ev) => {
      ev.preventDefault();
      len = Math.max(SNAP, Math.min(max, snap(len0 + (ev.clientY - y0) / PX)));
      setDrag({ id: item.id, kind: 'resize', len });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDrag(null);
      if (len !== len0) onResize(item, len);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  /** Shift+arrows on a focused block: a day sideways, 15 minutes up or down. */
  const blockKey = (e, item) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(item); return; }
    if (!e.shiftKey || busy) return;
    const d = dayOf(item);
    if (!d) return;
    const move = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -SNAP], ArrowDown: [0, SNAP] }[e.key];
    if (!move) return;
    e.preventDefault();
    const day = isoDay(new Date(parseDay(d).getFullYear(), parseDay(d).getMonth(), parseDay(d).getDate() + move[0]));
    const t = toMinutes(item.due_time);
    if (move[1] && t === null) return;
    const time = t === null ? null : toClock(Math.max(0, Math.min(24 * 60 - SNAP, t + move[1])));
    onMove(item, { due_date: day, due_time: time });
    // Keep the focus on the block where it lands.
    requestAnimationFrame(() => {
      const el = root.current && root.current.querySelector(`[data-cal-item="${item.id}"]`);
      if (el) el.focus();
    });
  };

  /** A press on an empty part of a column: a to-do at that time, on release without a drag. */
  const emptyDown = (e, day) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target !== e.currentTarget && !e.target.classList.contains('tcal-slot-line')) return;
    const r = e.currentTarget.getBoundingClientRect();
    const min = hours.from + (e.clientY - r.top) / PX;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const up = (ev) => {
      window.removeEventListener('pointerup', up);
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) > DRAG_AFTER) return;
      const at = Math.max(0, Math.min(24 * 60 - 30, Math.floor(min / 30) * 30));
      onCreate({ due_date: day, due_time: toClock(at), list_id: filter.list || null,
                 labels: filter.label ? [filter.label] : [], company_id: filter.client || null });
    };
    window.addEventListener('pointerup', up);
  };

  const createOn = (day) => onCreate({ due_date: day, due_time: null, list_id: filter.list || null,
                                       labels: filter.label ? [filter.label] : [], company_id: filter.client || null });

  /* ------------------------------------------------------------ the pieces */

  const listOf = (t) => lists.find((l) => l.id === t.list_id) || null;
  const p = (t) => Number(t.priority) || 4;

  const deadlineFlag = (t, { compact = false } = {}) => {
    const st = deadlineState(t, todayIso);
    return (
      <button key={`dl-${t.id}`} type="button" className={`tcal-flag is-${st || 'later'}`} data-cal-deadline={t.id}
              title={`Deadline: ${t.title}${st === 'late' ? ' (passed)' : ''}`}
              aria-label={`Deadline for "${t.title}"${st === 'late' ? ', passed' : ''}`}
              onClick={(e) => { e.stopPropagation(); onOpen(t); }}>
        <Icon name="deadline" size={12} />
        <span className="tcal-flag-text">{compact ? t.title : `Deadline · ${t.title}`}</span>
      </button>
    );
  };

  const untimedChip = (t) => {
    const l = listOf(t);
    return (
      <div key={t.id} className={`tcal-chip is-p${p(t)}${drag && drag.id === t.id ? ' is-dragging' : ''}`}
           data-cal-item={t.id} role="button" tabIndex={0}
           aria-label={`${t.title}, ${longDay(dayOf(t))}, no time${t.duration_minutes ? `, ${durationLabel(t.duration_minutes)}` : ''}. Open, or drag to a time`}
           onPointerDown={(e) => startMove(e, t)} onKeyDown={(e) => blockKey(e, t)}>
        <span className={`tcal-chip-dot ${swatch(l?.color)}`} aria-hidden="true" />
        <span className="tcal-chip-title">{t.title}</span>
        {t.repeat && <Icon name="repeat" size={11} className="tcal-rep" />}
        {t.duration_minutes ? <span className="tcal-chip-dur">{durationLabel(t.duration_minutes)}</span> : null}
      </div>
    );
  };

  const timedBlock = (t, lay) => {
    const b = blockOf(t);
    const len = drag && drag.id === t.id && drag.kind === 'resize' ? drag.len : b.end - b.start;
    const l = listOf(t);
    const top = (b.start - hours.from) * PX;
    const height = Math.max(20, len * PX - 2);
    const short = len < 45;
    const end = toClock(b.start + len);
    const st = deadlineState(t, todayIso);
    return (
      <div key={t.id} className={`tcal-block is-task is-p${p(t)} ${swatch(l?.color)}${short ? ' is-short' : ''}${drag && drag.id === t.id && drag.kind === 'move' ? ' is-dragging' : ''}`}
           data-cal-item={t.id} role="button" tabIndex={0}
           style={{ top, height, left: `calc(${(lay.col / lay.cols) * 100}% + 2px)`, width: `calc(${100 / lay.cols}% - 4px)` }}
           aria-label={`${t.title}, ${longDay(dayOf(t))} ${span(t.due_time, end)}. Open; Shift and the arrows move it`}
           title={`${t.title}\n${span(t.due_time, end)}${t.duration_minutes ? '' : ' (no duration set: shown as half an hour)'}`}
           onPointerDown={(e) => startMove(e, t, { grabMin: Math.max(0, (e.clientY - e.currentTarget.getBoundingClientRect().top) / PX) })}
           onKeyDown={(e) => blockKey(e, t)}>
        <span className="tcal-block-title">
          {t.repeat && <Icon name="repeat" size={11} className="tcal-rep" />}
          {t.title}
        </span>
        {!short && (
          <span className="tcal-block-time">
            {span(t.due_time, end)}
            {st === 'late' || st === 'soon' ? <Icon name="deadline" size={11} className={`tcal-block-dl is-${st}`} /> : null}
          </span>
        )}
        {canDuration && (
          <span className="tcal-resize" data-cal-resize={t.id} aria-hidden="true" title="Drag to change how long it takes"
                onPointerDown={(e) => startResize(e, t)} />
        )}
      </div>
    );
  };

  /** A feed event's chip: the all-day row, Month, wherever a line will do. */
  const feedChip = (f, day, { month = false } = {}) => (
    <button key={`${f.key}:${day}`} type="button" className={`tcal-fev ${swatch(f.color)}${month ? ' is-month' : ''}`}
            data-cal-feed={f.id} data-cal-feed-layer={f.feed_id}
            aria-label={`${f.title}, ${feedWhen(f)}, from ${f.layer.name}. Show the details`}
            title={`${f.title} · ${f.layer.name}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); openFeed(f, e.currentTarget); }}>
      <Icon name="calendar" size={11} className="tcal-fev-ic" />
      {month && !f.allDay && <span className="tcal-mtime">{clockOf(f.starts_at)}</span>}
      <span className="tcal-chip-title">{f.title}</span>
    </button>
  );

  const eventBlock = (ev, lay) => {
    const top = (ev.start - hours.from) * PX;
    const height = Math.max(20, (ev.end - ev.start) * PX - 2);
    const short = ev.end - ev.start < 45;
    if (ev.kind === 'feed') {
      const until = clockOf(ev.real_end && ev.real_end !== ev.starts_at ? ev.real_end : ev.ends_at);
      return (
        <button key={`${ev.key}:${ev.start}`} type="button"
                className={`tcal-block is-feed ${swatch(ev.color)}${short ? ' is-short' : ''}`}
                data-cal-feed={ev.id} data-cal-feed-layer={ev.feed_id}
                style={{ top, height, left: `calc(${(lay.col / lay.cols) * 100}% + 2px)`, width: `calc(${100 / lay.cols}% - 4px)` }}
                aria-label={`${ev.title}, ${feedWhen(ev)}, from ${ev.layer.name}. Show the details`}
                title={`${ev.title}\n${span(clockOf(ev.starts_at), until)}${ev.location ? ` · ${ev.location}` : ''}\n${ev.layer.name}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => openFeed(ev, e.currentTarget)}>
          <span className="tcal-block-title">
            <Icon name="calendar" size={11} className="tcal-ev-ic" />
            {ev.title}
          </span>
          {!short && <span className="tcal-block-time">{span(clockOf(ev.starts_at), until)}{ev.location ? ` · ${ev.location}` : ''}</span>}
        </button>
      );
    }
    return (
      <a key={`${ev.key}:${ev.start}`} href={ev.href} className={`tcal-block is-${ev.kind}${short ? ' is-short' : ''}${ev.response === 'tentative' ? ' is-tentative' : ''}`}
         data-cal-event={ev.key} data-cal-kind={ev.kind}
         style={{ top, height, left: `calc(${(lay.col / lay.cols) * 100}% + 2px)`, width: `calc(${100 / lay.cols}% - 4px)` }}
         title={`${ev.kind === 'meeting' ? 'Meeting' : 'Appointment'}: ${ev.title}\n${span(clockOf(ev.starts_at), clockOf(ev.ends_at))} · ${ev.where}`}
         onPointerDown={(e) => e.stopPropagation()}
         onClick={(e) => { if (onGo) { e.preventDefault(); onGo(ev.href); } }}>
        <span className="tcal-block-title">
          <Icon name={ev.kind === 'meeting' ? 'meeting' : 'ticket'} size={11} className="tcal-ev-ic" />
          {ev.title}
        </span>
        {!short && <span className="tcal-block-time">{span(clockOf(ev.starts_at), clockOf(ev.ends_at))} · {ev.where}</span>}
      </a>
    );
  };

  /** Where a dragged block would land, drawn in its column. */
  const ghostIn = (day) => {
    if (!drag || drag.kind !== 'move' || !drag.target || drag.target.day !== day || !drag.target.time) return null;
    const start = toMinutes(drag.target.time);
    return (
      <div className="tcal-ghost" aria-hidden="true"
           style={{ top: (start - hours.from) * PX, height: Math.max(20, drag.len * PX - 2) }}>
        <span>{span(drag.target.time, toClock(start + drag.len))}</span>
      </div>
    );
  };

  const head = (d) => {
    const dt = parseDay(d);
    const isToday = d === todayIso;
    return (
      <div key={d} className={`tcal-dayhead${isToday ? ' is-today' : ''}${dt.getDay() % 6 === 0 ? ' is-weekend' : ''}`}
           data-cal-head={d}>
        <span className="tcal-dayname">{WD[dt.getDay()]}</span>
        <span className="tcal-daynum" aria-label={longDay(d)}>{dt.getDate()}</span>
        <button type="button" className="tcal-add" aria-label={`Add a to-do on ${longDay(d)}`} title="Add a to-do on this day"
                disabled={busy} onClick={() => createOn(d)}><Icon name="plus" size={12} /></button>
      </div>
    );
  };

  /* ------------------------------------------------------------------ grid */

  const nowMin = now.getHours() * 60 + now.getMinutes();
  // A weekend day with nothing on it is half as wide (a week only).
  const colsOf = days.map((d) => {
    const v = byDay.get(d);
    const empty = !v.timed.length && !v.untimed.length && !v.deadlines.length && !v.events.length && !v.feeds.length;
    return days.length === 7 && empty && parseDay(d).getDay() % 6 === 0 ? 'minmax(0, 0.55fr)' : 'minmax(0, 1fr)';
  }).join(' ');
  const gridBlock = () => {
    const height = (hours.to - hours.from) * PX;
    const hourMarks = [];
    for (let h = Math.ceil(hours.from / 60); h * 60 < hours.to; h += 1) hourMarks.push(h);
    return (
      <div className="tcal-week" style={{ '--tcal-days': days.length, '--tcal-cols': colsOf }}>
        <div className="tcal-row tcal-heads">
          <div className="tcal-gutter" aria-hidden="true" />
          {days.map(head)}
        </div>
        <div className="tcal-row tcal-allday" role="group" aria-label="All day: to-dos with no time, deadlines and all-day events">
          <div className="tcal-gutter tcal-allday-label">All day</div>
          {days.map((d) => {
            const v = byDay.get(d);
            const over = drag && drag.kind === 'move' && drag.target && drag.target.day === d && !drag.target.time;
            return (
              <div key={d} className={`tcal-allday-cell${over ? ' is-over' : ''}${d === todayIso ? ' is-today' : ''}`}
                   data-cal-allday={d}>
                {v.feeds.map((f) => feedChip(f, d))}
                {v.deadlines.map((t) => deadlineFlag(t, { compact: true }))}
                {v.untimed.map(untimedChip)}
              </div>
            );
          })}
        </div>
        <div className="tcal-scroll" ref={scroller}>
          <div className="tcal-row tcal-grid" style={{ height }}>
            <div className="tcal-gutter tcal-hours" aria-hidden="true">
              {hourMarks.map((h) => (
                <span key={h} className="tcal-hour" style={{ top: (h * 60 - hours.from) * PX }}>{`${String(h).padStart(2, '0')}:00`}</span>
              ))}
            </div>
            {days.map((d) => {
              const v = byDay.get(d);
              const laid = layoutDay([
                ...v.timed.map((t) => ({ key: `t:${t.id}`, t, ...blockOf(t) })),
                ...v.events.map((ev) => ({ key: `${ev.key}:${ev.start}`, ev, start: ev.start, end: ev.end }))
              ]);
              return (
                <div key={d} className={`tcal-col${d === todayIso ? ' is-today' : ''}${parseDay(d).getDay() % 6 === 0 ? ' is-weekend' : ''}`}
                     data-cal-day={d} style={{ '--tcal-hour': `${HOUR_PX}px`, '--tcal-offset': `${-(hours.from % 60) * PX}px` }}
                     aria-label={`${longDay(d)}: click an empty time to add a to-do`}
                     onPointerDown={(e) => emptyDown(e, d)}>
                  {laid.map((x) => (x.t ? timedBlock(x.t, x) : eventBlock(x.ev, x)))}
                  {ghostIn(d)}
                  {d === todayIso && nowMin >= hours.from && nowMin <= hours.to && (
                    <div className="tcal-now" style={{ top: (nowMin - hours.from) * PX }} aria-hidden="true" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ----------------------------------------------------------------- month */

  const monthBlock = () => {
    const anchorMonth = parseDay(anchor).getMonth();
    return (
      <div className="tcal-month" role="grid" aria-label={rangeTitle('month', days)}>
        <div className="tcal-mrow tcal-mheads" role="row">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => (
            <div key={w} className="tcal-mhead" role="columnheader">{w}</div>
          ))}
        </div>
        {Array.from({ length: days.length / 7 }, (_, w) => (
          <div key={w} className="tcal-mrow" role="row">
            {days.slice(w * 7, w * 7 + 7).map((d) => {
              const v = byDay.get(d);
              const entries = [
                ...v.feeds.map((f) => ({ key: f.key, at: -2, f })),
                ...v.events.map((ev) => (ev.kind === 'feed' ? { key: ev.key, at: ev.start, f: ev } : { key: ev.key, at: ev.start, ev })),
                ...v.timed.map((t) => ({ key: t.id, at: toMinutes(t.due_time), t })),
                ...v.untimed.map((t) => ({ key: t.id, at: -1, t }))
              ].sort((a, b) => a.at - b.at);
              const flags = v.deadlines;
              const room = Math.max(0, SHOW_IN_MONTH - flags.length);
              const more = entries.length - room;
              const dt = parseDay(d);
              const over = drag && drag.kind === 'move' && drag.target && drag.target.day === d;
              return (
                <div key={d} role="gridcell" data-cal-mday={d}
                     className={`tcal-mcell${dt.getMonth() !== anchorMonth ? ' is-other' : ''}${d === todayIso ? ' is-today' : ''}${over ? ' is-over' : ''}`}
                     onPointerDown={(e) => {
                       if (e.target !== e.currentTarget) return;
                       const x0 = e.clientX;
                       const y0 = e.clientY;
                       const up = (ev) => {
                         window.removeEventListener('pointerup', up);
                         if (Math.hypot(ev.clientX - x0, ev.clientY - y0) <= DRAG_AFTER) createOn(d);
                       };
                       window.addEventListener('pointerup', up);
                     }}>
                  <button type="button" className="tcal-mnum" aria-label={`${longDay(d)}: show the week`}
                          onClick={() => { setAnchor(d); setView('week'); }}>{dt.getDate()}</button>
                  {flags.map((t) => deadlineFlag(t, { compact: true }))}
                  {entries.slice(0, room).map((x) => (x.f ? feedChip(x.f, d, { month: true }) : x.ev ? (
                    <a key={x.key} href={x.ev.href} className={`tcal-mev is-${x.ev.kind}`} data-cal-event={x.ev.key}
                       onClick={(e) => { if (onGo) { e.preventDefault(); onGo(x.ev.href); } }}
                       title={`${x.ev.title} · ${span(clockOf(x.ev.starts_at), clockOf(x.ev.ends_at))}`}>
                      <span className="tcal-mtime">{clockOf(x.ev.starts_at)}</span>{x.ev.title}
                    </a>
                  ) : (
                    <div key={x.key} className={`tcal-mtodo is-p${p(x.t)}${drag && drag.id === x.t.id ? ' is-dragging' : ''}`}
                         data-cal-item={x.t.id} role="button" tabIndex={0}
                         aria-label={`${x.t.title}${x.t.due_time ? ` at ${x.t.due_time}` : ''}. Open, or drag to another day`}
                         onPointerDown={(e) => startMove(e, x.t)} onKeyDown={(e) => blockKey(e, x.t)}>
                      <span className={`tcal-chip-dot ${swatch(listOf(x.t)?.color)}`} aria-hidden="true" />
                      {x.t.due_time && <span className="tcal-mtime">{x.t.due_time}</span>}
                      <span className="tcal-chip-title">{x.t.title}</span>
                    </div>
                  )))}
                  {more > 0 && (
                    <button type="button" className="tcal-more" onClick={() => { setAnchor(d); setView('day'); }}>
                      {`+${more} more`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  /* ---------------------------------------------------------------- agenda */

  const agendaBlock = () => (
    <ol className="tcal-agenda" aria-label="Agenda">
      {days.map((d) => {
        const v = byDay.get(d);
        const rows = [
          ...v.feeds.map((f) => ({ key: f.key, at: -2, f })),
          ...v.events.map((ev) => (ev.kind === 'feed' ? { key: ev.key, at: ev.start, f: ev } : { key: ev.key, at: ev.start, ev })),
          ...v.timed.map((t) => ({ key: t.id, at: toMinutes(t.due_time), t })),
          ...v.untimed.map((t) => ({ key: t.id, at: -1, t }))
        ].sort((a, b) => a.at - b.at);
        if (!rows.length && !v.deadlines.length && d !== todayIso) return null;
        return (
          <li key={d} className={`tcal-aday${d === todayIso ? ' is-today' : ''}`} data-cal-aday={d}>
            <h4 className="tcal-ahead">
              <span>{d === todayIso ? `Today · ${longDay(d)}` : longDay(d)}</span>
              <button type="button" className="tcal-add" aria-label={`Add a to-do on ${longDay(d)}`}
                      disabled={busy} onClick={() => createOn(d)}><Icon name="plus" size={12} /></button>
            </h4>
            {v.deadlines.map((t) => deadlineFlag(t))}
            {!rows.length && !v.deadlines.length && <p className="tcal-quiet">Nothing planned.</p>}
            <ul className="tcal-alist">
              {rows.map((x) => (x.f ? (
                <li key={`${x.key}:${d}`}>
                  <button type="button" className={`tcal-arow is-feed ${swatch(x.f.color)}`} data-cal-feed={x.f.id}
                          data-cal-feed-layer={x.f.feed_id}
                          aria-label={`${x.f.title}, ${feedWhen(x.f)}, from ${x.f.layer.name}. Show the details`}
                          onClick={(e) => openFeed(x.f, e.currentTarget)}>
                    <span className="tcal-atime">{x.f.allDay ? 'All day'
                      : span(clockOf(x.f.starts_at), clockOf(x.f.real_end && x.f.real_end !== x.f.starts_at ? x.f.real_end : x.f.ends_at))}</span>
                    <span className="tcal-atitle"><Icon name="calendar" size={12} className="tcal-fev-ic" /> {x.f.title}</span>
                    <span className="tcal-awhere">{x.f.layer.name}</span>
                  </button>
                </li>
              ) : x.ev ? (
                <li key={x.key}>
                  <a href={x.ev.href} className={`tcal-arow is-${x.ev.kind}`} data-cal-event={x.ev.key}
                     onClick={(e) => { if (onGo) { e.preventDefault(); onGo(x.ev.href); } }}>
                    <span className="tcal-atime">{span(clockOf(x.ev.starts_at), clockOf(x.ev.ends_at))}</span>
                    <span className="tcal-atitle"><Icon name={x.ev.kind === 'meeting' ? 'meeting' : 'ticket'} size={12} /> {x.ev.title}</span>
                    <span className="tcal-awhere">{x.ev.where}</span>
                  </a>
                </li>
              ) : (
                <li key={x.key}>
                  <button type="button" className={`tcal-arow is-task is-p${p(x.t)}`} data-cal-item={x.t.id}
                          onClick={() => onOpen(x.t)}>
                    <span className="tcal-atime">{x.t.due_time
                      ? span(x.t.due_time, toClock(blockOf(x.t).end)) : 'Any time'}</span>
                    <span className="tcal-atitle">
                      <span className={`tcal-chip-dot ${swatch(listOf(x.t)?.color)}`} aria-hidden="true" />
                      {x.t.title}{x.t.repeat && <Icon name="repeat" size={11} className="tcal-rep" />}
                    </span>
                    {x.t.duration_minutes ? <span className="tcal-awhere">{durationLabel(x.t.duration_minutes)}</span> : null}
                  </button>
                </li>
              )))}
            </ul>
          </li>
        );
      })}
    </ol>
  );

  /* ------------------------------------------------------------------ page */

  const title = view === 'agenda' ? `${longDay(from)} – ${longDay(to)}` : rangeTitle(view, days);
  const isThisRange = days.includes(todayIso);
  const clients = (companies || []).filter((c) => !c.archived || items.some((t) => t.company_id === c.id));
  const filtered = Boolean(filter.list || filter.label || filter.client);
  const anything = [...byDay.values()].some((v) => v.timed.length || v.untimed.length || v.deadlines.length || v.events.length
    || v.feeds.length);
  const anyLayerOn = layers.some((l) => l.on);
  const picker = layers.length > 0 && saveLayers
    ? <LayersPicker layers={layers} onToggle={toggleLayer} saving={layersSaving} /> : null;
  // 097: a private address for Outlook, Apple or Google Calendar to follow.
  const subscribeBtn = subscribe ? <CalendarSubscribe subscribe={subscribe} narrow={narrow} /> : null;

  return (
    <div className={`tcal is-${view}${drag ? ' is-dragging' : ''}`} ref={root} data-cal-view={view}>
      <div className="tcal-bar">
        <span className="tcal-nav" role="group" aria-label="Move through the calendar">
          <button type="button" className="btn btn-secondary btn-small tcal-today" disabled={isThisRange && view !== 'month'}
                  onClick={goToday} title="Back to today (T)">Today</button>
          <button type="button" className="tcal-step" aria-label="Earlier" title="Earlier (←)" onClick={() => go(-1)}>
            <Icon name="chevronLeft" size={16} />
          </button>
          <button type="button" className="tcal-step" aria-label="Later" title="Later (→)" onClick={() => go(1)}>
            <Icon name="chevronRight" size={16} />
          </button>
        </span>
        <h3 className="tcal-title" aria-live="polite">{title}</h3>
        <span className="tcal-views" role="group" aria-label="Show as">
          {allowed.map((v) => (
            <button key={v.key} type="button" aria-pressed={view === v.key} data-cal-mode={v.key}
                    onClick={() => setView(v.key)}>{v.label}</button>
          ))}
        </span>
      </div>

      {narrow && (
        <div className="tcal-narrow-tools">
          <button type="button" className="btn btn-secondary btn-small tcal-filter-toggle" aria-expanded={showFilters}
                  onClick={() => setShowFilters((v) => !v)}>
            {filtered ? `Filter (${[filter.list, filter.label, filter.client].filter(Boolean).length})` : 'Filter'}
          </button>
          {picker}
          {subscribeBtn}
        </div>
      )}
      {(!narrow || showFilters) && (
      <div className="tcal-filters" role="group" aria-label="Show only">
        <label className="tcal-filter"><span>List</span>
          <select aria-label="Show the to-dos of one list" value={filter.list}
                  onChange={(e) => setFilter((f) => ({ ...f, list: e.target.value }))}>
            <option value="">Every list</option>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        {labels.length > 0 && (
          <label className="tcal-filter"><span>Label</span>
            <select aria-label="Show the to-dos with one label" value={filter.label}
                    onChange={(e) => setFilter((f) => ({ ...f, label: e.target.value }))}>
              <option value="">Any label</option>
              {labels.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        )}
        {clients.length > 0 && (
          <label className="tcal-filter"><span>Client</span>
            <select aria-label="Show the to-dos for one client" value={filter.client}
                    onChange={(e) => setFilter((f) => ({ ...f, client: e.target.value }))}>
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        {filtered && (
          <button type="button" className="tda-add-line tcal-clear" onClick={() => setFilter({ list: '', label: '', client: '' })}>
            Show everything
          </button>
        )}
        {!narrow && picker}
        {!narrow && subscribeBtn}
        <span className="tcal-legend" aria-hidden="true">
          <span className="tcal-key is-task" />To-do
          <span className="tcal-key is-meeting" />Meeting
          <span className="tcal-key is-appointment" />Appointment
          <Icon name="deadline" size={12} className="tcal-key-ic" />Deadline
          {anyLayerOn && <><span className="tcal-key is-feed" />Event</>}
        </span>
      </div>
      )}

      {feedNote && <p className="tcal-note" role="status">{feedNote}</p>}

      {view === 'month' ? monthBlock() : view === 'agenda' ? agendaBlock() : gridBlock()}

      {drag && drag.kind === 'move' && view === 'month' && (
        <div className="tcal-float" style={{ left: drag.x + 10, top: drag.y + 8 }} aria-hidden="true">{drag.title}</div>
      )}

      {pop && (
        <FeedPopover item={pop.item} at={pop.at} sheet={narrow} onClose={closePop} busy={busy}
                     onAdd={onAddEvent ? (draft) => onAddEvent(draft, pop.item) : null} />
      )}

      <p className="tcal-foot">
        {!anything && <span className="tcal-quiet">{filtered ? 'Nothing here matches the filter. ' : 'Nothing planned here yet. '}</span>}
        A repeating to-do shows its next date only.
        {!canDuration && ' Every block is half an hour until durations are set up on this server.'}
        {canDuration && !narrow && ' Drag a block to move it, its bottom edge to change how long it takes; click an empty time to add one.'}
      </p>
    </div>
  );
}

